import { lazy, Suspense, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Layout } from './pages/Layout';
import { RemoteAppShell } from './components/RemoteAppShell';
import { AuthGate } from './components/AuthGate';
import { QueryBoundary } from './components/QueryBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { FullPageWandLoader } from './components/FullPageWandLoader';
import { useNavigationRoutes } from './routes/navigationRoutes';
import {
  isRemoteApp,
  isIFrameApp,
  isNewTabApp,
  detectPspHostingEnvironment,
} from './services/utils';
import { useSegmentAnalytics } from './hooks/useSegmentAnalytics';
import { useSegmentIdentify } from './hooks/useSegmentIdentify';
import { useOgAnalytics } from './hooks/useOgAnalytics';
import { useAhaFeedback } from './hooks/useAhaFeedback';
import { useCspViolationReporter } from './hooks/useCspViolationReporter';
import { useAuth0Broadcast } from './hooks/useAuth0Broadcast';
import { useSessionExpiryGuard } from './hooks/useSessionExpiryGuard';
import { useTraceUserAttributes } from './hooks/useTraceUserAttributes';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { AllProductsPage } from './pages/AllProductsPage';
import { shouldShowComingSoon } from './pages/comingSoonUtils';
import { useFeatureFlag, FeatureFlagKey } from './hooks/useFeatureFlags';
import { useShouldShowOnboarding } from './hooks/useShouldShowOnboarding';
import { useAuth } from './hooks/useAuth';
import { getIconComponent } from './utils/icons';
import {
  AppConfig,
  RemoteModuleConfig,
  ResolvedRouteRegistryEntry,
  RouteRegistryId,
} from './types';
import { IFrameAppShellWrapper } from './components/IFrameAppShell/IFrameAppShellWrapper';
import { EntityProvider } from './contexts/EntityContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { PspContextProvider, usePspContext } from './contexts/PspContext';
import { useLoggerWithContext } from './services/logger/loggerContext';
import { handleOnboardingModuleLoadFailure } from './services/onboarding/handleOnboardingModuleLoadFailure';
import { useAuth0 } from '@auth0/auth0-react';
import { extractSafeUserContext } from './utils/auth0UserUtils';
import { LaunchDarklyProvider } from './contexts/LaunchDarklyContext';

// Dev-only LD toolbar. The conditional lazy() ensures Vite drops the import in prod builds.
const LaunchDarklyDevToolbar = import.meta.env.DEV
  ? lazy(() =>
      import('./components/LaunchDarklyDevToolbar').then((m) => ({
        default: m.LaunchDarklyDevToolbar,
      }))
    )
  : null;

export function AppRoutes() {
  const navigationRoutes = useNavigationRoutes();
  const { appRegistry, routeRegistry } = usePspContext();
  const allProductsEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_ALL_PRODUCTS_PAGE);
  const shouldShowOnboarding = useShouldShowOnboarding();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.sub;

  // Escape hatch for `/onboarding` load/mount failure. Comment out the
  // `onModuleError` prop below to inspect the standard RemoteAppShell error page.
  const handleOnboardingModuleError = useCallback(() => {
    handleOnboardingModuleLoadFailure(userId, () => navigate('/', { replace: true }));
  }, [navigate, userId]);

  if (shouldShowOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <Routes>
      {/* Main Application Routes - Auto-generated from navigationConfig */}
      {navigationRoutes}

      {/* Remote Applications - Dynamically generated from appRegistry */}
      {appRegistry.map((app: AppConfig) => {
        // A navigation-only entry is served by a route registry entry (gab-core apps by the
        // shared `/a` mount); a route of its own would outrank that one.
        if (app.navigationOnly) return null;

        // Check if app is an iframe app (has externalUrl but not openInNewTab)
        if (isIFrameApp(app)) {
          return (
            <Route
              key={app.displayName}
              path={`${app.basePath}/*`}
              element={
                <IFrameAppShellWrapper defaultUrl={app.externalUrl} title={app.displayName} />
              }
            />
          );
        }

        // New-tab apps have no in-shell route — the sidebar opens their externalUrl
        // in a new tab. This also covers a flag-gated tab-out (e.g. PLC when
        // psp-enable-plc is off), whose moduleRoutes resolver returns '' and must
        // not be mounted. Direct navigation to the basePath falls to the catch-all.
        if (isNewTabApp(app)) {
          return null;
        }

        // Show "Coming Soon" for entitled apps without valid moduleUrl and no component
        if (shouldShowComingSoon(app)) {
          return (
            <Route
              key={app.displayName}
              path={`${app.basePath}/*`}
              element={
                <ComingSoonPage appName={app.displayName} icon={getIconComponent(app.icon)} />
              }
            />
          );
        }

        // Only render RemoteAppShell if app is a remote app with moduleRoutes defined
        if (
          isRemoteApp(app) &&
          'moduleRoutes' in app &&
          app.moduleRoutes &&
          app.moduleRoutes.length > 0
        ) {
          const remoteConfig: RemoteModuleConfig = {
            id: app.id,
            basePath: app.basePath,
            moduleRoutes: app.moduleRoutes,
            moduleConfig: app.moduleConfig,
          };
          return (
            <Route
              key={app.displayName}
              path={`${app.basePath}/*`}
              element={
                // `key={app.id}` on the element (NOT just on the Route) is
                // critical: react-router v6 renders only the matching Route's
                // `element`, and when the route changes from /foo/* to /bar/*
                // both elements are `<RemoteAppShell>` of the same component
                // type. Without an element-level key, React preserves the
                // RemoteAppShell instance and just updates props — so the
                // previous app's `module` state survives the swap, and the
                // new shell briefly mounts the OLD module into the NEW
                // container at the NEW URL. The old app's router then sees
                // a URL outside its basename, fires its `<Route path="*">`
                // catch-all `<Navigate>`, and pollutes window.location with
                // a segment from its own routes. Explicit element key forces
                // a clean remount per app.
                <RemoteAppShell
                  key={app.basePath}
                  moduleConfig={remoteConfig}
                  showDebugInfo={import.meta.env.DEV}
                />
              }
            />
          );
        }

        // Fallback: shouldn't reach here for apps with components (handled by navigationRoutes)
        return null;
      })}

      {/* Route Registry - Non-navigation routes (accessible via links, dropdowns, etc.) */}
      {routeRegistry.map((route: ResolvedRouteRegistryEntry) => {
        // Render local component if defined
        if (route.component) {
          const Component = route.component;
          return (
            <Route
              key={`route-registry-${route.id}`}
              path={`${route.basePath}/*`}
              element={
                <QueryBoundary
                  loadingFallback={<LoadingSpinner />}
                  errorMessage="Failed to load page"
                >
                  <Component />
                </QueryBoundary>
              }
            />
          );
        }

        // Render remote module if moduleRoutes defined
        if (route.moduleRoutes && route.moduleRoutes.length > 0) {
          const remoteConfig: RemoteModuleConfig = {
            id: route.id,
            basePath: route.basePath,
            moduleRoutes: route.moduleRoutes,
            moduleConfig: route.moduleConfig,
          };
          const isOnboarding = route.id === RouteRegistryId.ONBOARDING;
          return (
            <Route
              key={`route-registry-${route.id}`}
              path={`${route.basePath}/*`}
              element={
                // See app-registry branch above for why `key` is required
                // on the element itself, not just on the <Route>.
                <RemoteAppShell
                  key={`route-${route.id}`}
                  moduleConfig={remoteConfig}
                  showDebugInfo={import.meta.env.DEV}
                  fullPage={route.fullPage}
                  // Comment out to verify the standard module load error page;
                  // restore to confirm bad deploys redirect home instead of locking users.
                  onModuleError={isOnboarding ? handleOnboardingModuleError : undefined}
                />
              }
            />
          );
        }

        return null;
      })}

      {/* All Products page - flag-gated. When off, the route isn't registered
          and the catch-all below redirects /all-products to home. */}
      {allProductsEnabled && (
        <Route
          path="/all-products"
          element={
            <QueryBoundary
              loadingFallback={<LoadingSpinner />}
              errorMessage="Failed to load products"
            >
              <AllProductsPage />
            </QueryBoundary>
          }
        />
      )}

      {/* Catch-all route - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppInitialization() {
  const logger = useLoggerWithContext();
  const { user, isAuthenticated } = useAuth0();
  const { pathname } = useLocation();

  // Inject the Segment analytics.js snippet (flag-gated via LD; must be inside LaunchDarklyProvider)
  useSegmentAnalytics();
  // Identify visitor and account to Segment (flows downstream to Pendo)
  useSegmentIdentify();
  // @opengov/analytics — runs alongside Segment. The SDK reads its own
  // configuration from the environment; nothing is named here. Gated by the
  // `psp-og-analytics` LD flag. `signed_in` is recorded from Auth0's
  // onRedirectCallback in main.tsx.
  useOgAnalytics();
  // Tag Faro traces with current user (email, full name) and entity friendlyId
  useTraceUserAttributes();
  // Inject Aha! feedback widget and identify user
  useAhaFeedback();
  // Listen for CSP violations in report-only mode and log to Grafana Faro
  useCspViolationReporter();
  // Broadcast Auth0 state changes onto the shell event bus so mounted ES
  // modules using @opengov/shell-auth0-react stay in sync without remounting.
  useAuth0Broadcast();
  // Redirect to Auth0 login when the session is truly dead (silent refresh
  // failed). Mounted modules never see isAuthenticated: false.
  useSessionExpiryGuard();

  // Log application initialization on mount
  useEffect(() => {
    logger.info(
      'PSP application initialized',
      {
        environment: detectPspHostingEnvironment(),
        window_host: window.location.host,
        has_session_storage: !!window.sessionStorage.length,
        has_local_storage: !!window.localStorage.length,
      },
      { persistContext: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Update persistent context when user authenticates
  useEffect(() => {
    if (isAuthenticated && user) {
      const userContext = extractSafeUserContext(user);
      if (userContext) {
        logger.info('User authenticated', userContext, { persistContext: true });
      }
    }
  }, [isAuthenticated, user, logger]);

  // Log when authentication state changes (could indicate multi-tab issues)
  useEffect(() => {
    if (!isAuthenticated && user) {
      logger.warn('Authentication state mismatch: user exists but not authenticated', {
        auth_event: 'auth_state_mismatch',
        user_id: user.sub,
        current_path: pathname,
      });
    }
  }, [isAuthenticated, user, logger, pathname]);

  // Update persistent context when path changes
  useEffect(() => {
    logger.info('Navigated to new page', { path: pathname }, { persistContext: true });
  }, [pathname, logger]);

  return null;
}

export function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthGate>
        <QueryBoundary
          loadingFallback={<FullPageWandLoader />}
          errorMessage="Failed to load entity data"
        >
          <EntityProvider>
            <LaunchDarklyProvider>
              {LaunchDarklyDevToolbar && (
                <Suspense fallback={null}>
                  <LaunchDarklyDevToolbar />
                </Suspense>
              )}
              {/* DEV TOOL ONLY: Central control prop for whether to apply dev tool overrides. Does not hide the dev tool page, it only suppresses the dev tool overrides from being applied. */}
              <PspContextProvider allowDevToolOverrides={true}>
                <AppInitialization />
                <FavoritesProvider>
                  <Layout>
                    <AppRoutes />
                  </Layout>
                </FavoritesProvider>
              </PspContextProvider>
            </LaunchDarklyProvider>
          </EntityProvider>
        </QueryBoundary>
      </AuthGate>
    </BrowserRouter>
  );
}
